/**
 * @author jakubbujakowski
 * @since 2022-9-1
 */

import * as React from "react";
import styled from "styled-components";

import { IS_DEV } from "@config/consts";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { rem } from "@ui/helpers";

import { StepDebugger } from "./Progressbar.helpers";

interface Step {
  label?: string;
  route?: string; // optional because it's only used for DEV debugging
}

interface Props {
  testId?: string;
  className?: string;
  activeStep: number;
  steps: Step[];
}

const Progressbar = (props: Props): React.ReactElement => {
  const stepWidth = 100 / props.steps.length;
  const currentLabel = props.steps[props.activeStep]?.label;

  const [showDebugger, setShowDebugger] = React.useState(false);

  const handleDevClick = () => {
    if (!IS_DEV) return;
    setShowDebugger(val => !val);
  };

  return (
    <TestWrapper testId={props.testId}>
      <ProgressbarWrapper className={props.className} onClick={handleDevClick}>
        {currentLabel && <Title>{currentLabel}</Title>}
        <ProgressContainer
          activeStep={props.activeStep + 1}
          stepWidth={stepWidth}
        >
          {IS_DEV && showDebugger && <StepDebugger steps={props.steps} />}
        </ProgressContainer>
      </ProgressbarWrapper>
    </TestWrapper>
  );
};

export { Progressbar };

const ProgressbarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  display: none;

  ${props => props.theme.breakpoints.desktop} {
    display: inline-block;
    font-size: ${props => props.theme.fonts.sizes.h6};
    font-weight: ${props => props.theme.fonts.weights.semibold};
    margin-bottom: ${props => props.theme.margins.base};
  }
`;

const ProgressContainer = styled.div<{ stepWidth: number; activeStep: number }>`
  position: relative;
  height: ${rem(4)};
  width: 100%;
  background-color: ${props => props.theme.palette.grey100};

  &::after {
    content: "";
    position: absolute;
    height: 100%;
    background-color: ${props => props.theme.palette.green500};
    width: ${props => props.stepWidth}%;
    z-index: 1;
    transform: scaleX(${props => props.activeStep});
    transform-origin: left;
  }
`;
